import { useRef, useState } from 'react';

import { usePropsDataStore } from '@store/PropsDataStore';

import { usePropertyFetcher } from '@hooks/usePropertyFetcher';

import { generateProp, getProp, searchProps } from '@services/property';

import { cn } from '@utils/cn';

import { Button, Icon, Input, TwSelector as Selector, Text } from '@ui-component-library';

import { DynamicModal } from '@components/DynamicModal';

import type { SearchPropertyResult } from '../../PropertySelector.types';

import { AlertDialog } from '../AlertModal';
import { SkeletonLoader } from '../SkeletonLoaders';

import type { SearchAndAddPropertiesProps } from './SearchAndAddProperties.types';

const SearchAndAddProperties = ({
  className,
  isOpen,
  onClose,
  onSelect,
  onAddSuccess
}: SearchAndAddPropertiesProps) => {
  const isSelectOnlyMode = !!onSelect;
  const { props: properties, setOneProp } = usePropsDataStore();
  const { checkPropStatus } = usePropertyFetcher();

  const [selected, setSelected] = useState<SearchPropertyResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingProp, setIsFetchingProp] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchPropertyResults, setSearchPropertyResults] = useState<SearchPropertyResult[]>([]);
  const latestSearchQueryRef = useRef<string>('');
  const hasEmptySearchResults = hasSearched && searchPropertyResults.length === 0;

  const [errorMessage, setErrorMessage] = useState(undefined);

  const handleSelect = (option: { label: string; value: string }) => {
    const selectedProperty = searchPropertyResults.find(
      (property) => property.registerenhet === option.value
    );
    setSelected(selectedProperty);
  };

  const cleanupStates = () => {
    setSelected(null);
    setSearchPropertyResults([]);
    setHasSearched(false);
    latestSearchQueryRef.current = '';
  };

  const handleClose = () => {
    onClose();
    cleanupStates();
  };

  const handleSearch = async (value: string) => {
    if (value === '') {
      cleanupStates();
      return;
    }
    latestSearchQueryRef.current = value;

    if (value.length < 3 || latestSearchQueryRef.current !== value) {
      return;
    }

    setIsLoading(true);
    const result = await searchProps(value);

    const mappedResults: SearchPropertyResult[] = result.data.map((item) => {
      const isDisabled = properties.some((property) => property.ref === item.registerenhet);

      return {
        ...item,
        beteckning: item.beteckning.toLowerCase(),
        disabled: isDisabled
      };
    });

    setSearchPropertyResults(
      mappedResults.sort((a, b) => a.beteckning.localeCompare(b.beteckning))
    );
    setHasSearched(true);
    setIsLoading(false);
  };

  const handleCloseErrorModal = () => {
    setErrorMessage(undefined);
  };

  const generateProperty = async () => {
    try {
      setIsFetchingProp(true);
      const response = await getProp(selected?.registerenhet);
      const generatedResponse = await generateProp({
        geometry: response.data.geojson.geometry,
        prop: selected
      });

      if (generatedResponse.data.status === 'to_small') {
        setErrorMessage({
          title: 'Fastigheten är för liten för en skogsbruksplan',
          subtitle:
            'Valda fastigheten är mindre än den minsta storlek som krävs för att skapa en skogsbruksplan.',
          designation: selected?.beteckning,
          helperInfo: 'Du kan lägga till en annan fastighet - eller kontakta oss för rådgivning.'
        });
        return;
      }

      if (generatedResponse.data.data.prop_big_area) {
        const areaText = generatedResponse.data.data.prop_big_area
          ? `${generatedResponse.data.data.prop_big_area} ha`
          : 'stor';
        setErrorMessage({
          title: 'Stor fastighet genereras',
          subtitle: `Fastighetens area är ${areaText} och kan ta lång tid att generera. Du får ett mail när fastigheten är klar.`,
          designation: selected?.beteckning,
          helperInfo: 'Om fastigheten inte genereras inom 24 timmar, kontakta supporten.'
        });
      }

      setOneProp(generatedResponse.data.data);
      checkPropStatus(setOneProp, generatedResponse.data.data);
      handleClose();
      onAddSuccess?.();
    } catch (_error) {
      setErrorMessage({
        title: 'Skogsbruksplanen kunde inte skapas just nu',
        subtitle: 'Ett problem uppstod när vi skapade skogsbruksplanen för:',
        designation: selected?.beteckning,
        helperInfo:
          'Prova gärna igen vid ett senare tillfälle. Om problemet kvarstår, kontakta supporten.',
        onPrimaryAction: () => {
          setErrorMessage(undefined);
          generateProperty();
        },
        primaryActionText: 'Försök igen'
      });
    } finally {
      setIsFetchingProp(false);
    }
  };

  const getButtonText = () => {
    if (isFetchingProp && !isSelectOnlyMode) {
      return 'Laddar...';
    }
    return 'Lägg till fastighet';
  };

  const handlePrimaryAction = () => {
    if (isSelectOnlyMode && selected) {
      onSelect(selected);
      handleClose();
    } else {
      generateProperty();
    }
  };

  return (
    <>
      <DynamicModal
        isOpen={isOpen}
        onClose={onClose}
        className={cn('flex-col', className)}
        bp={{ sm: 'drawer', lg: 'sheet' }}
        drawerSize="full"
        hideBackdrop={true}
      >
        <DynamicModal.Header className="">
          <div>
            <Text variant="text-lg/leading-normal/medium">Sök i Lantmäteriet</Text>
            <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
              Ange fastighetbeteckning
            </Text>
          </div>
          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
          </Button>
        </DynamicModal.Header>
        <DynamicModal.Content className="p-0">
          <div className="bg-white-50 sticky top-0 border-b border-solid border-[#E6E6E6] p-4">
            <Input
              variant="fluid"
              name="search"
              label="T.ex. Ydre Sevedstorp 1:8"
              onChange={handleSearch}
              prefix={<Icon name="MagnifyingGlassSimple" size="medium" />}
            />
            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
              Visar fastigheter från Lantmäteriets register
            </Text>
          </div>
          <div className="flex flex-col gap-2 p-4">
            {isLoading && (
              <div className="mt-6 flex flex-col gap-3">
                <SkeletonLoader length={10} type="searchResult" />
              </div>
            )}
            {!isLoading && hasEmptySearchResults && (
              <Text variant="text-sm/leading-normal/medium" className="mt-6 px-4 text-[#737373]">
                Inga resultat hittades
              </Text>
            )}
            {!isLoading && searchPropertyResults.length > 0 && (
              <>
                <Text variant="text-xs/leading-normal/medium" className="px-4 text-[#737373]">
                  Förslag
                </Text>

                <Selector>
                  {searchPropertyResults.map((property) => (
                    <Selector.Option
                      key={property.objektidentitet}
                      label={
                        property.disabled
                          ? ` ${property.beteckning} - tillagd`
                          : property.beteckning
                      }
                      value={property.registerenhet}
                      isSelected={selected?.registerenhet === property.registerenhet}
                      disabled={property.disabled}
                      onClick={handleSelect}
                      className="capitalize"
                    />
                  ))}
                </Selector>
              </>
            )}
          </div>
        </DynamicModal.Content>
        <DynamicModal.Footer className="flex flex-col gap-3 lg:flex-col-reverse">
          <Button fullWidth variant="primary" disabled={!selected} onClick={handlePrimaryAction}>
            {!isFetchingProp && <Icon name="PlusSimple" className="fill-[#FBFBFB]" />}
            <Text variant="text-sm/leading-normal/medium">{getButtonText()}</Text>
            {!isSelectOnlyMode && isFetchingProp && (
              <div className="border-white-50 h-4 w-4 animate-spin rounded-full border-2 border-solid border-t-transparent" />
            )}
          </Button>
        </DynamicModal.Footer>
      </DynamicModal>

      {errorMessage && (
        <AlertDialog
          {...errorMessage}
          isOpen={!!errorMessage}
          onClose={handleCloseErrorModal}
          size="small"
        />
      )}
    </>
  );
};

export default SearchAndAddProperties;
